import { type ReactNode, useState, useEffect, useRef } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

// ── Sample notifications (replace with API call as needed) ──
const SAMPLE_NOTIFICATIONS = [
  { id: 1, icon: "bi-bag-check",    color: "#ec4899", text: "New order #1042 received",           time: "2 min ago",  unread: true  },
  { id: 2, icon: "bi-star-fill",    color: "#f59e0b", text: "Priya Sharma left a 5★ review",      time: "15 min ago", unread: true  },
  { id: 3, icon: "bi-chat-quote",   color: "#8b5cf6", text: "New quote request from Anjali M.",   time: "1 hr ago",   unread: true  },
  { id: 4, icon: "bi-boxes",        color: "#10b981", text: "Stock low: Diamond Pendant (3 left)", time: "3 hr ago",  unread: false },
  { id: 5, icon: "bi-envelope",     color: "#3b82f6", text: "Newsletter: 12 new subscribers",     time: "Yesterday",  unread: false },
];

type AdminLayoutProps = { children: ReactNode };

const masterItems = [
  { to: "/admin/master/create-category",     label: "Create Category",     icon: "bi-tags" },
  { to: "/admin/master/create-sub-category", label: "Create Sub Category", icon: "bi-diagram-3" },
  { to: "/admin/master/create-brand",        label: "Create Brand",        icon: "bi-award" },
  { to: "/admin/master/create-material",     label: "Create Material",     icon: "bi-layers" },
  { to: "/admin/master/create-material-type", label: "Material Type",       icon: "bi-tags" },
  { to: "/admin/master/create-status",        label: "Status",              icon: "bi-toggle-on" },
  { to: "/admin/master/create-stone-type",    label: "Stone Type",          icon: "bi-gem" },
  { to: "/admin/master/create-occasion-type", label: "Occasion Type",       icon: "bi-calendar-event" },
  { to: "/admin/master/create-product-tag",   label: "Product Tags",        icon: "bi-tag" },
   { to: "/admin/master/create-tags",     label: "Create Tags",     icon: "bi-tags" },

];

const navGroups = [
  { section: "Main", items: [
    { to: "/admin/dashboard",     label: "Dashboard",     icon: "bi-grid-1x2" },
    { to: "/admin/analytics",     label: "Analytics",     icon: "bi-graph-up-arrow" },
    { to: "/admin/notifications", label: "Notifications", icon: "bi-bell" },
  ]},
  { section: "Catalogue", items: [
    { to: "/admin/products",  label: "Products",  icon: "bi-gem" },
    { to: "/admin/inventory", label: "Inventory", icon: "bi-boxes" },
    { to: "/admin/banners",   label: "Banners",   icon: "bi-image" },
  ]},
  { section: "Sales", items: [
    { to: "/admin/orders",      label: "Orders",         icon: "bi-bag-check" },
    { to: "/admin/returns",     label: "Returns",        icon: "bi-arrow-return-left" },
    { to: "/admin/payments",    label: "Payments",       icon: "bi-credit-card-2-front" },
    { to: "/admin/coupons",     label: "Coupons",        icon: "bi-ticket-perforated" },
    { to: "/admin/gift-cards",  label: "Gift Cards",     icon: "bi-gift" },
    { to: "/admin/carts",       label: "Cart Management",icon: "bi-cart3" },
    { to: "/admin/flash-sales", label: "Flash Sales",    icon: "bi-lightning-charge" },
    { to: "/admin/wishlists",      label: "Wishlists",      icon: "bi-heart" },
    { to: "/admin/loyalty-points", label: "Loyalty Points",  icon: "bi-trophy" },
  ]},
  { section: "Customers", items: [
    { to: "/admin/users",     label: "Users",     icon: "bi-person-gear" },
    { to: "/admin/customers", label: "Customers", icon: "bi-people" },
    { to: "/admin/reviews",   label: "Reviews",   icon: "bi-star" },
  ]},
  { section: "Content", items: [
    { to: "/admin/blog",            label: "Blog",            icon: "bi-pencil-square" },
    { to: "/admin/faq",             label: "FAQ",             icon: "bi-patch-question" },
    { to: "/admin/quote-requests",  label: "Quote Requests",  icon: "bi-chat-quote" },
    { to: "/admin/newsletter",      label: "Newsletter",      icon: "bi-envelope-paper" },
    { to: "/admin/testimonials",    label: "Testimonials",    icon: "bi-chat-quote-fill" },
    { to: "/admin/advertisement-banners", label: "Ad Banners", icon: "bi-image" },
  ]},
  { section: "Store", items: [
    { to: "/admin/reports",    label: "Reports",         icon: "bi-bar-chart-line" },
    { to: "/admin/tax",        label: "Tax / GST",       icon: "bi-percent" },
    { to: "/admin/shipping",   label: "Shipping Zones",  icon: "bi-truck" },
    { to: "/admin/navigation", label: "Navigation Menu", icon: "bi-menu-button-wide" },
    { to: "/admin/seo",        label: "SEO Settings",    icon: "bi-search" },
    { to: "/admin/settings",   label: "Settings",        icon: "bi-gear" },
  ]},
];

const pageTitles: Record<string, { title: string; sub: string }> = {
  "/admin/users":                       { title: "User Management",          sub: "Create & manage admin users" },
  "/admin/dashboard":                   { title: "Dashboard",                sub: "Overview & key metrics" },
  "/admin/analytics":                   { title: "Analytics",                sub: "Sales trends & reports" },
  "/admin/notifications":               { title: "Notifications",            sub: "Alerts & activity" },
  "/admin/products":                    { title: "Product Management",       sub: "Manage your jewellery catalogue" },
  "/admin/categories":                  { title: "Category Management",      sub: "Organise product categories" },
  "/admin/inventory":                   { title: "Inventory & Stock",        sub: "Track stock levels" },
  "/admin/banners":                     { title: "Banners & Hero Slides",    sub: "Homepage visuals" },
  "/admin/orders":                      { title: "Order Management",         sub: "Track & manage orders" },
  "/admin/returns":                     { title: "Return & Refund Requests", sub: "Process customer returns" },
  "/admin/coupons":                     { title: "Coupon Management",        sub: "Discount codes & offers" },
  "/admin/carts":                       { title: "Cart Management",          sub: "View & manage customer carts" },
  "/admin/flash-sales":                 { title: "Flash Sales & Offers",     sub: "Time-limited promotions" },
  "/admin/customers":                   { title: "Customer Management",      sub: "Customer profiles & data" },
  "/admin/reviews":                     { title: "Reviews & Ratings",        sub: "Manage customer reviews" },
  "/admin/shipping":                    { title: "Shipping & Delivery Zones",sub: "Configure delivery areas" },
  "/admin/seo":                         { title: "SEO & Meta Settings",      sub: "Search engine optimisation" },
  "/admin/settings":                    { title: "Store Settings",           sub: "Configure your store" },
  "/admin/master/create-category":      { title: "Create Category",          sub: "Add a new product category" },
  "/admin/master/create-sub-category":  { title: "Create Sub Category",      sub: "Add a new sub-category" },
  "/admin/master/create-brand":         { title: "Create Brand",             sub: "Add a new brand" },
  "/admin/blog":                        { title: "Blog",                     sub: "Write & publish blog posts" },
  "/admin/faq":                         { title: "FAQ Manager",              sub: "Manage frequently asked questions" },
  "/admin/quote-requests":              { title: "Quote Requests",           sub: "Manage free quote form submissions" },
  "/admin/newsletter":                  { title: "Newsletter",               sub: "Manage email subscribers" },
  "/admin/navigation":                  { title: "Navigation Menu",          sub: "Manage site header menu & dropdowns" },
  "/admin/testimonials":                { title: "Testimonials",             sub: "Manage customer reviews & ratings" },
  "/admin/master/create-material":      { title: "Create Material",          sub: "Add a new material type" },
  "/admin/master/create-material-type": { title: "Material Type",             sub: "Manage material type master" },
  "/admin/master/create-tags": { title: "Material Type",             sub: "Manage material type master" },
  "/admin/master/create-status":        { title: "Status",                    sub: "Manage product status master" },
  "/admin/master/create-stone-type":    { title: "Stone Type",                sub: "Manage stone type master" },
  "/admin/master/create-occasion-type": { title: "Occasion Type",             sub: "Manage occasion type master" },
  "/admin/master/create-product-tag":   { title: "Product Tags",              sub: "Manage product tags master" },
  "/admin/advertisement-banners":       { title: "Advertisement Banners",     sub: "Manage promotional banners with offer text" },
  "/admin/payments":                    { title: "Payments & Transactions",  sub: "Track payments, refunds & statuses" },
  "/admin/reports":                     { title: "Reports & Analytics",      sub: "Sales charts & revenue insights" },
  "/admin/wishlists":                   { title: "Wishlist Manager",         sub: "View user wishlists & notify stock" },
  "/admin/tax":                         { title: "Tax / GST Settings",       sub: "GST slabs & state-wise tax rules" },
  "/admin/gift-cards":                  { title: "Gift Cards",               sub: "Generate & manage gift cards" },
  "/admin/loyalty-points":              { title: "Loyalty & Rewards",        sub: "Manage points, tiers & redemptions" },
};

export function AdminLayout({ children }: AdminLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed]   = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile]     = useState(window.innerWidth < 768);
  const [masterOpen, setMasterOpen] = useState(false);
  const [notifOpen, setNotifOpen]   = useState(false);
  const masterRef                   = useRef<HTMLDivElement>(null);
  const notifRef                    = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Close notification dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isMasterActive = masterItems.some(i => location.pathname === i.to);
  const pageInfo = pageTitles[location.pathname] ?? { title: "Admin", sub: "NineSistersJewels" };

  function onLogout() { logout(); navigate("/admin/login", { replace: true }); }

  const initials = user?.name
    ? user.name.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase()
    : "A";

  const sidebarClass = [
    "ns-sidebar",
    collapsed && !isMobile ? "collapsed" : "",
    isMobile && mobileOpen ? "mobile-open" : "",
  ].filter(Boolean).join(" ");

  const mainClass = ["ns-main", !isMobile && collapsed ? "collapsed" : ""].filter(Boolean).join(" ");

  return (
    <div className="ns-layout">
      {isMobile && mobileOpen && <div onClick={() => setMobileOpen(false)} className="ns-mobile-overlay" />}

      <aside className={sidebarClass}>
        <div className="ns-sidebar-brand">
          <div className="ns-sidebar-logo-icon">💎</div>
          <div className="ns-sidebar-brand-text">
            <div className="brand-name"><span>Nine</span>Sisters</div>
            <div className="brand-tag">Admin Panel 2026</div>
          </div>
        </div>

        <nav className="ns-sidebar-nav">

          {navGroups.map((group, idx) => (
            <div key={group.section}>
              <div className="ns-nav-section-label">{group.section}</div>
              {group.items.map(item => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-label={item.label}
                  className={({ isActive }) => `ns-nav-link ${isActive ? "active" : ""}`}
                  onClick={() => isMobile && setMobileOpen(false)}
                >
                  <i className={`bi ${item.icon} nav-icon`}></i>
                  <span className="nav-label">{item.label}</span>
                </NavLink>
              ))}

              {/* ── MASTER DROPDOWN — renders after Catalogue (index 1) ── */}
              {idx === 1 && (
                <div ref={masterRef}>
                  <div className="ns-nav-section-label">Master</div>
                  <button
                    type="button"
                    className={`ns-nav-link ns-master-toggle ${isMasterActive ? "active" : ""}`}
                    onClick={() => setMasterOpen(o => !o)}
                    data-label="Master"
                  >
                    <i className="bi bi-collection nav-icon"></i>
                    <span className="nav-label">Master</span>
                    <i
                      className={`bi nav-chevron ${(masterOpen || isMasterActive) ? "bi-chevron-up" : "bi-chevron-down"}`}
                    ></i>
                  </button>

                  <div className={`ns-master-dropdown ${(masterOpen || isMasterActive) ? "open" : ""}`}>
                    {masterItems.map(item => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        className={({ isActive }) => `ns-nav-link ns-master-child ${isActive ? "active" : ""}`}
                        data-label={item.label}
                      >
                        <i className={`bi ${item.icon} nav-icon`}></i>
                        <span className="nav-label">{item.label}</span>
                      </NavLink>
                    ))}
                  </div>
                </div>
              )}
              {/* ── END MASTER ── */}
            </div>
          ))}
        </nav>

        <div className="ns-sidebar-footer">
          <button
            type="button"
            onClick={onLogout}
            className={`ns-nav-link ns-sidebar-logout ${collapsed && !isMobile ? "justify-center" : ""}`}
            data-label="Logout"
          >
            <i className="bi bi-box-arrow-left nav-icon ns-logout-icon"></i>
            <span className="nav-label ns-logout-icon">Logout</span>
          </button>
          <div className="ns-sidebar-user ns-sidebar-user-mt">
            <div className="ns-user-avatar">{initials}</div>
            <div className="ns-user-info">
              <div className="u-name">{user?.name ?? "Admin"}</div>
              <div className="u-role">{user?.role ?? "admin"}</div>
            </div>
          </div>
        </div>
      </aside>

      <div className={mainClass}>
        <header className="ns-header">
          <div className="ns-header-left">
            <button
              className="ns-toggle-btn"
              type="button"
              onClick={() => isMobile ? setMobileOpen(o => !o) : setCollapsed(c => !c)}
              title="Toggle sidebar"
            >
              <i className={`bi ${collapsed && !isMobile ? "bi-layout-sidebar-inset" : "bi-layout-sidebar"}`}></i>
            </button>
            <div className="ns-breadcrumb">
              <div className="ns-page-title">{pageInfo.title}</div>
              <div className="ns-page-sub">{pageInfo.sub}</div>
            </div>
          </div>

          <div className="ns-header-right">
            {/* ── Notification Bell with Dropdown ── */}
            <div ref={notifRef} style={{ position: "relative" }}>
              <button
                className="ns-hdr-btn"
                title="Notifications"
                type="button"
                onClick={() => setNotifOpen(o => !o)}
                style={{ position: "relative" }}
              >
                <i className="bi bi-bell"></i>
                {SAMPLE_NOTIFICATIONS.some(n => n.unread) && (
                  <span className="notif-dot"></span>
                )}
              </button>

              {notifOpen && (
                <div style={{
                  position: "absolute", top: "calc(100% + 10px)", right: 0,
                  width: 340, background: "#fff", borderRadius: 16,
                  boxShadow: "0 12px 40px rgba(0,0,0,.15)", border: "1px solid #f1f5f9",
                  zIndex: 9999, overflow: "hidden", animation: "mIn .2s ease",
                }}>
                  {/* Dropdown Header */}
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", borderBottom: "1px solid #f1f5f9" }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: "#1e293b" }}>Notifications</div>
                      <div style={{ fontSize: 11, color: "#94a3b8" }}>
                        {SAMPLE_NOTIFICATIONS.filter(n => n.unread).length} unread
                      </div>
                    </div>
                    <Link
                      to="/admin/notifications"
                      onClick={() => setNotifOpen(false)}
                      style={{ fontSize: 12, fontWeight: 600, color: "#ec4899", textDecoration: "none" }}
                    >
                      View All
                    </Link>
                  </div>

                  {/* Notification List */}
                  <div style={{ maxHeight: 320, overflowY: "auto" }}>
                    {SAMPLE_NOTIFICATIONS.map(n => (
                      <div key={n.id} style={{
                        display: "flex", alignItems: "flex-start", gap: 12, padding: "12px 16px",
                        background: n.unread ? "#fdf4ff" : "#fff",
                        borderBottom: "1px solid #f8fafc", cursor: "pointer",
                        transition: "background .15s",
                      }}
                        onMouseEnter={e => (e.currentTarget.style.background = "#f8fafc")}
                        onMouseLeave={e => (e.currentTarget.style.background = n.unread ? "#fdf4ff" : "#fff")}
                      >
                        <div style={{
                          width: 34, height: 34, borderRadius: 10, flexShrink: 0,
                          background: n.color + "18", display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                          <i className={`bi ${n.icon}`} style={{ color: n.color, fontSize: 15 }}></i>
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, color: "#1e293b", fontWeight: n.unread ? 600 : 400, lineHeight: 1.4 }}>
                            {n.text}
                          </div>
                          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{n.time}</div>
                        </div>
                        {n.unread && (
                          <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#ec4899", flexShrink: 0, marginTop: 4 }} />
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Dropdown Footer — redirect to full page */}
                  <div style={{ padding: "10px 16px", borderTop: "1px solid #f1f5f9", textAlign: "center" }}>
                    <Link
                      to="/admin/notifications"
                      onClick={() => setNotifOpen(false)}
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 6,
                        fontSize: 13, fontWeight: 600, color: "#475569",
                        textDecoration: "none", padding: "6px 16px",
                        borderRadius: 8, background: "#f8fafc", border: "1px solid #e2e8f0",
                      }}
                    >
                      <i className="bi bi-arrow-right-circle"></i>
                      Go to Notifications Page
                    </Link>
                  </div>
                </div>
              )}
            </div>
            <button className="ns-hdr-btn" title="Search" type="button">
              <i className="bi bi-search"></i>
            </button>
            <span className="ns-role-pill">{user?.role ?? "admin"}</span>
            <div className="ns-user-chip" title={user?.email}>
              <div className="ns-user-chip-avatar">{initials}</div>
              <span className="ns-user-chip-name">{user?.name ?? "Admin"}</span>
            </div>
            <button className="ns-logout-btn" onClick={onLogout} type="button">
              <i className="bi bi-box-arrow-right"></i>
              Logout
            </button>
          </div>
        </header>

        <main className="ns-content page-enter">{children}</main>
      </div>
    </div>
  );
}
